import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "../../../auth/auth.service";
import { DownloaderService } from "../../../core/downloader/downloader.service";
import { MessagingService } from "../../../core/messaging/messaging.service";
import { SeverityType } from "../../../core/messaging/severity-type.enum";
import { PopoverComponent } from "../../../shared/panel/popover/popover.component";
import { ArrayHelper } from "../../../utilities/contracts/array-helper";
import { StaggerHelper } from "../../../utilities/contracts/stagger-helper";
import { StringHelper } from "../../../utilities/contracts/string-helper";
import { ChartService } from "../../api/chart-lake/chart.service";
import { ProviderService } from "../../api/provider/provider.service";
import { AlertNotification } from "../../notification/alert-notification.model";
import { NotificationType } from "../../notification/notification-type.enum";
import { NotificationService } from "../../notification/notification.service";

@Component({
  selector: "platform-notifications",
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private intervalUpdateCount;
  notifications: AlertNotification[];
  read: boolean;
  unreadNotificationCount = 0;
  private readonly destroy$ = new Subject<void>();

  @ViewChild("overlay", { static: true }) private overlay: PopoverComponent;


  constructor(
    private readonly service: NotificationService,
    private authService: AuthService,
    private chartService: ChartService,
    private providerService: ProviderService,
    private downloaderService: DownloaderService,
    private readonly messagingService: MessagingService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.service.eventCallback$.subscribe(data => {
      this.unreadNotificationCount = data;
      this.changeDetector.markForCheck();
  });
    this.getUnreadNotificationCount();
    this.setupPollingForRole();

    // This shell component is never destroyed on navigation, and account switching
    // no longer forces a full page reload, so a role change on switch must
    // re-evaluate whether polling should be enabled.
    this.authService.userTokenChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        clearInterval(this.intervalUpdateCount);
        this.getUnreadNotificationCount();
        this.setupPollingForRole();
      });
  }

  ngOnDestroy() {
    clearInterval(this.intervalUpdateCount);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupPollingForRole(): void {
    if ((this.authService.user.isEmployeeRole && this.authService.user.isAdminRole) || !this.authService.user.isEmployeeRole) {
      this.intervalUpdateCount = setInterval(() => {
        this.getUnreadNotificationCount();
      },                                     120000);
    }
  }


  get hasUnreadNotifications(): boolean {
    return this.unreadNotificationCount > 0;
  }

  get unreadAndActiveClass(): string {
    const unread = this.hasUnreadNotifications ? "notifications__unread" : "";
    const active = this.overlay.visible ? "notifications__active" : "";
    return `${unread} ${active}`.trim();
  }

  get countText(): string {
    return this.hasUnreadNotifications ? this.unreadNotificationCount.toString() : "";
  }

  show(event: MouseEvent) {
    if (!this.overlay.visible) {
      this.getNotificationsAndShow(event);
      this.markAllAsRead();
    }
  }


  private getUnreadNotificationCount() {
    this.service
      .getNotificationCount()
      .subscribe(items => {
        this.unreadNotificationCount = items;
        this.changeDetector.markForCheck();
      });
  }

  private getNotificationsAndShow(event: MouseEvent): void {
    this.service
      .getNotification()
      .subscribe(items => {
        this.notifications = items;
        this.overlay.show(event);
        this.changeDetector.markForCheck();
      });
  }

  private markAllAsRead(): void {
    this.service.ReadNotification().subscribe();
    this.read = false;
    this.unreadNotificationCount = 0;
    this.changeDetector.markForCheck();
  }


  trackByIndex(index, item) {
    return index;
  }

  isMentionAlert(item: AlertNotification): boolean {
    return item.name === NotificationType.Mention && !!item.supplemental && ArrayHelper.isAvailable(item.supplemental.qualifiers);
  }

  onDownloadChartClick(event: Event, item: AlertNotification, overlay: PopoverComponent): void {
    event.preventDefault();
    const jobId = item.supplemental?.qualifiers?.find(q => q.name === "jobId")?.id;
    if (StringHelper.isAvailable(jobId) && item?.name === NotificationType.Success) {
      this.chartService.getPresignedDownloadUrl(jobId).subscribe((url: string) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    }
    overlay.hide();
  }

  onPrintPacketDownloadClick(event: Event, item: AlertNotification, overlay: PopoverComponent): void {
    event.preventDefault();
    const jobId = this.bulkActionQualifier(item, "jobId");
    if (StringHelper.isAvailable(jobId) && item?.name === NotificationType.Success) {
      this.providerService.getPrintPacketJobFiles(jobId).subscribe({
        next: jobStatus => {
          StaggerHelper.run(jobStatus?.files ?? [], 2000, file =>
            this.downloaderService.fromPath(`file/download?fileType=providerPacket&fileId=${file.fileName}`));
        },
        error: () => this.messagingService.showMessage("Unable to retrieve the print packet files. Please try again.", SeverityType.ERROR),
      });
    }
    overlay.hide();
  }

  private bulkActionQualifier(item: AlertNotification, name: string): string {
    return item.supplemental?.qualifiers?.find(q => q.name === name)?.id;
  }

  hasBulkActionJob(item: AlertNotification): boolean {
    return StringHelper.isAvailable(this.bulkActionQualifier(item, "jobId"));
  }

  bulkActionRoute(item: AlertNotification): any[] {
    return ["/bulkactions/bulkactionresults", this.bulkActionQualifier(item, "jobId")];
  }

  bulkActionQueryParams(item: AlertNotification): { [key: string]: string } {
    const phase = this.bulkActionQualifier(item, "phase");
    return StringHelper.isAvailable(phase) ? { phase } : {};
  }

}
